<script setup lang="ts">
import type { ImageOrientation } from '~kui/utils/richTextMedia';

import { Text } from '~ds/components/ui/typography';
import { IMAGE_ORIENTATION } from '~kui/utils/richTextMedia';

interface Props {
  src: string;
  alt?: string;
  caption?: string;
  orientation?: ImageOrientation;
  width?: number | null;
  height?: number | null;
  sizes?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  alt: '',
  caption: '',
  orientation: IMAGE_ORIENTATION.Landscape,
  width: null,
  height: null,
  sizes: null,
});

const isPortrait = computed(() => props.orientation === IMAGE_ORIENTATION.Portrait);

// A portrait figure is capped at max-w-sm wherever it sits, so the caller's `sizes` (its column
// width) only applies to landscape images.
const pictureSizes = computed(() => (isPortrait.value ? '100vw sm:384px' : props.sizes ?? '100vw md:768px'));

const figureClasses = computed(() => twMerge(cva('w-full', {
  variants: {
    portrait: {
      true: 'max-w-sm',
      false: '',
    },
  },
})({ portrait: isPortrait.value })));
</script>

<template>
  <figure
    :class="figureClasses"
    :data-orientation="orientation"
    data-testid="rte-renderer-image"
  >
    <NuxtPicture
      :src="src"
      format="avif,webp"
      :sizes="pictureSizes"
      loading="lazy"
      :img-attrs="{ alt, width: width ?? undefined, height: height ?? undefined, class: 'h-auto w-full rounded-lg' }"
    />
    <Text
      v-if="caption"
      as="figcaption"
      size="sm"
      class="mt-2 text-muted-foreground"
    >
      {{ caption }}
    </Text>
  </figure>
</template>
